/* global google */
import { EFlightAreaType } from '../types/flight-area'
import pin19be6b from '/@/assets/icons/pin-19be6b.svg'
import pin212121 from '/@/assets/icons/pin-212121.svg'
import pin2d8cf0 from '/@/assets/icons/pin-2d8cf0.svg'
import pinb620e0 from '/@/assets/icons/pin-b620e0.svg'
import pine23c39 from '/@/assets/icons/pin-e23c39.svg'
import pineffbb00 from '/@/assets/icons/pin-ffbb00.svg'
import { getRoot } from '/@/root'
import rootStore from '/@/store'
import { GeojsonCoordinate } from '/@/types/map'

// Type for cover data
interface CoverData {
  id: string
  name?: string
  type?: string
  [key: string]: any
}

export function useGMapCover () {
  const root = getRoot()

  const normalColor = '#2D8CF0'
  const store = rootStore
  const coverMap = store.state.coverMap

  const flightAreaColorMap = {
    [EFlightAreaType.DFENCE]: '#19be6b',
    [EFlightAreaType.NFZ]: '#ff0000',
  }
  const disableColor = '#b3b3b3'

  function getMap (): any {
    return root.$map
  }

  function addCoverToMap (cover: any, data: CoverData) {
    const map = getMap()
    if (!map) return

    cover.setMap(map)
    cover._extData = data
    coverMap[data.id] = [cover]
  }

  function addOverlayGroup (overlay: any, data: CoverData) {
    const map = getMap()
    if (!map) return

    overlay.setMap(map)
    overlay._extData = data
    const id = data.id
    coverMap[id] = [...(coverMap[id] || []), overlay]
  }

  function getExtData (overlay: any): CoverData | null {
    return overlay._extData || null
  }

  function getPinIcon (color?: string): any {
    const colorObj: { [key: string]: string } = {
      '2d8cf0': pin2d8cf0,
      '19be6b': pin19be6b,
      212121: pin212121,
      b620e0: pinb620e0,
      e23c39: pine23c39,
      ffbb00: pineffbb00,
    }
    const iconName = (color?.replace(/#/g, '') || '2d8cf0').toLowerCase()
    return {
      url: colorObj[iconName] || pin2d8cf0,
      scaledSize: new google.maps.Size(40, 50),
      anchor: new google.maps.Point(20, 50),
    }
  }

  function init2DPin (name: string, coordinates: GeojsonCoordinate, color?: string, data?: CoverData) {
    const map = getMap()
    if (!map) return

    const marker = new google.maps.Marker({
      position: { lat: coordinates[1], lng: coordinates[0] },
      title: name,
      icon: getPinIcon(color),
      map: map,
    })

    const extData = data || { id: `pin_${Date.now()}`, name }
    marker._extData = extData
    coverMap[extData.id] = [marker]
  }

  function initPolyline (name: string, coordinates: GeojsonCoordinate[], color?: string, data?: CoverData) {
    const map = getMap()
    if (!map) return

    const path = coordinates.map(coord => ({
      lat: coord[1],
      lng: coord[0],
    }))

    const polyline = new google.maps.Polyline({
      path,
      strokeColor: color || normalColor,
      strokeOpacity: 1,
      strokeWeight: 2,
      map: map,
    })

    const extData = data || { id: `polyline_${Date.now()}`, name }
    addOverlayGroup(polyline, extData)
  }

  function initPolygon (name: string, coordinates: GeojsonCoordinate[][], color?: string, data?: CoverData) {
    const map = getMap()
    if (!map) return

    const path = coordinates[0].map(coord => ({
      lat: coord[1],
      lng: coord[0],
    }))

    const polygon = new google.maps.Polygon({
      paths: path,
      strokeColor: color || normalColor,
      strokeOpacity: 1,
      strokeWeight: 2,
      fillColor: color || normalColor,
      fillOpacity: 0.4,
      map: map,
    })

    const extData = data || { id: `polygon_${Date.now()}`, name }
    addOverlayGroup(polygon, extData)
  }

  function removeCoverFromMap (id: string) {
    if (!coverMap[id]) return

    coverMap[id].forEach((cover: any) => {
      cover.setMap(null)
    })
    coverMap[id] = []
  }

  function getElementFromMap (id: string): any[] {
    return coverMap[id] || []
  }

  function updatePinElement (id: string, name: string, coordinates: GeojsonCoordinate, color?: string) {
    const elements = getElementFromMap(id)
    if (elements && elements.length > 0) {
      const element = elements[0]
      element.setPosition({ lat: coordinates[1], lng: coordinates[0] })
      element.setIcon(getPinIcon(color))
      element.setTitle(name)
    } else {
      init2DPin(name, coordinates, color, { id, name })
    }
  }

  function updatePolylineElement (id: string, name: string, coordinates: GeojsonCoordinate[], color?: string) {
    const elements = getElementFromMap(id)
    if (elements && elements.length > 0) {
      const element = elements[0]
      element.setOptions({
        strokeColor: color || normalColor,
      })
    } else {
      initPolyline(name, coordinates, color, { id, name })
    }
  }

  function updatePolygonElement (id: string, name: string, coordinates: GeojsonCoordinate[][], color?: string) {
    const elements = getElementFromMap(id)
    if (elements && elements.length > 0) {
      const element = elements[0]
      element.setOptions({
        fillColor: color || normalColor,
        strokeColor: color || normalColor,
      })
    } else {
      initPolygon(name, coordinates, color, { id, name })
    }
  }

  function initTextInfo (content: string, coordinates: GeojsonCoordinate, id: string) {
    const map = getMap()
    if (!map) return

    const label = new google.maps.Marker({
      position: { lat: coordinates[1], lng: coordinates[0] },
      map: map,
      label: {
        text: content,
        color: '#000000',
        fontSize: '16px',
        fontWeight: 'bold',
      },
      icon: {
        path: google.maps.SymbolPath.CIRCLE,
        scale: 0,
      },
    })

    label._extData = { id, type: 'text' }
    addOverlayGroup(label, { id, type: 'text' })
  }

  function initFlightAreaCircle (
    name: string,
    radius: number,
    position: GeojsonCoordinate,
    data: { id: string; type: EFlightAreaType; enable: boolean }
  ) {
    const map = getMap()
    if (!map) return

    const circle = new google.maps.Circle({
      center: { lat: position[1], lng: position[0] },
      radius: radius,
      strokeColor: data.enable ? flightAreaColorMap[data.type] : disableColor,
      strokeOpacity: 1,
      strokeWeight: 6,
      fillColor: flightAreaColorMap[data.type],
      fillOpacity: data.type === EFlightAreaType.NFZ && data.enable ? 0.3 : 0,
      map: map,
    })

    circle._extData = data
    addOverlayGroup(circle, data)
    initTextInfo(name, position, data.id)
  }

  function updateFlightAreaCircle (
    id: string,
    name: string,
    radius: number,
    position: GeojsonCoordinate,
    enable: boolean,
    type: EFlightAreaType
  ) {
    const elements = getElementFromMap(id)
    if (elements && elements.length > 0) {
      let textIndex = elements.findIndex((ele: any) => getExtData(ele)?.type === 'text')

      if (textIndex === -1) {
        textIndex = 1
        initTextInfo(name, position, id)
      } else {
        const text = elements[textIndex]
        text.setLabel({
          text: name,
          color: '#000000',
          fontSize: '16px',
          fontWeight: 'bold',
        })
        text.setPosition({ lat: position[1], lng: position[0] })
      }

      const circleIndex = textIndex === 0 ? 1 : 0
      const circle = elements[circleIndex]

      circle.setOptions({
        fillOpacity: type === EFlightAreaType.NFZ && enable ? 0.3 : 0,
        strokeColor: enable ? flightAreaColorMap[type] : disableColor,
        radius: radius,
        center: { lat: position[1], lng: position[0] },
      })
    } else {
      initFlightAreaCircle(name, radius, position, { id, type, enable })
    }
  }

  function calcPolygonPosition (coordinates: GeojsonCoordinate[]): GeojsonCoordinate {
    if (coordinates.length === 0) return [0, 0]

    let sumLng = 0
    let sumLat = 0
    coordinates.forEach(coord => {
      sumLng += coord[0]
      sumLat += coord[1]
    })

    return [sumLng / coordinates.length, sumLat / coordinates.length]
  }

  function initFlightAreaPolygon (
    name: string,
    coordinates: GeojsonCoordinate[],
    data: { id: string; type: EFlightAreaType; enable: boolean }
  ) {
    const map = getMap()
    if (!map) return

    const path = coordinates.map(coord => ({
      lat: coord[1],
      lng: coord[0],
    }))

    const polygon = new google.maps.Polygon({
      paths: path,
      strokeColor: data.enable ? flightAreaColorMap[data.type] : disableColor,
      strokeOpacity: 1,
      strokeWeight: 4,
      fillColor: flightAreaColorMap[data.type],
      fillOpacity: data.type === EFlightAreaType.NFZ && data.enable ? 0.3 : 0,
      map: map,
      draggable: true,
    })

    polygon._extData = data
    addOverlayGroup(polygon, data)
    initTextInfo(name, calcPolygonPosition(coordinates), data.id)
  }

  function updateFlightAreaPolygon (
    id: string,
    name: string,
    coordinates: GeojsonCoordinate[],
    enable: boolean,
    type: EFlightAreaType
  ) {
    const elements = getElementFromMap(id)
    if (elements && elements.length > 0) {
      let textIndex = elements.findIndex((ele: any) => getExtData(ele)?.type === 'text')

      if (textIndex === -1) {
        textIndex = 1
        initTextInfo(name, calcPolygonPosition(coordinates), id)
      } else {
        const text = elements[textIndex]
        text.setLabel({
          text: name,
          color: '#000000',
          fontSize: '16px',
          fontWeight: 'bold',
        })
        const center = calcPolygonPosition(coordinates)
        text.setPosition({ lat: center[1], lng: center[0] })
      }

      const polygonIndex = textIndex === 0 ? 1 : 0
      const polygon = elements[polygonIndex]

      const path = coordinates.map(coord => ({
        lat: coord[1],
        lng: coord[0],
      }))

      polygon.setOptions({
        paths: path,
        fillOpacity: type === EFlightAreaType.NFZ && enable ? 0.3 : 0,
        strokeColor: enable ? flightAreaColorMap[type] : disableColor,
      })
    } else {
      initFlightAreaPolygon(name, coordinates, { id, type, enable })
    }
  }

  return {
    init2DPin,
    initPolyline,
    initPolygon,
    removeCoverFromMap,
    getElementFromMap,
    updatePinElement,
    updatePolylineElement,
    updatePolygonElement,
    initFlightAreaCircle,
    initFlightAreaPolygon,
    updateFlightAreaPolygon,
    updateFlightAreaCircle,
    calcPolygonPosition,
    getExtData,
  }
}
